import { formatNumber, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { EvilPieChart } from '../../../components/charts/charts/recharts-pie-chart.js';
import type { ChartConfig } from '../../../components/charts/ui/recharts-chart.js';
import type { OverviewInvoiceDue, OverviewStats } from '../overviewTypes.js';

import { HOME_CARD_LINK, HomeCard } from './HomeCard.js';

/**
 * Invoice health: how much of the book is cleared, and what the rest is doing.
 *
 * THIS USED TO DRAW ITS OWN SVG — one `<circle>` per segment, each positioned by accumulating
 * `strokeDasharray` offsets against a hand-computed circumference constant, rotated -90 degrees to
 * start at twelve o'clock. It is a donut, so it is now a donut, with a hover tooltip it never had.
 *
 * ⚠ THE CONFIG IS KEYED BY THE `nameKey` VALUES, not by a series name. The pie builds each sector's
 * gradient id from `item[nameKey]`, so a segment whose name has no config entry paints with no
 * fill. Here the colour is genuinely the legend — paid, overdue, partial and unpaid are four
 * different states, not four samples of one measure — so each keeps its own token.
 *
 * The cleared percentage moved into the header. The chart system draws labels per sector rather
 * than in the middle of the ring, and stacking an absolutely positioned figure over somebody else's
 * chart is how a number ends up a few pixels off centre and stays that way.
 */

/**
 * ONE map of state to token, for both the legend swatches and the donut.
 *
 * There were two, side by side, holding the same four keys and the same four tokens with nothing
 * connecting them. Editing one and not the other gave a legend naming a colour the ring did not
 * draw, and neither a typecheck nor a render would have said so. The chart config is DERIVED from
 * this below; the only thing it adds is the translated label.
 */
const SEGMENT_TOKEN = {
  paid: 'var(--success)',
  overdue: 'var(--destructive)',
  partial: 'var(--warning)',
  unpaid: 'var(--muted-foreground)',
} as const;

type SegmentKey = keyof typeof SEGMENT_TOKEN;

export function InvoiceStatusRing({
  invoices,
  stats,
}: {
  invoices: OverviewInvoiceDue[];
  stats: OverviewStats;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const partial = invoices.filter((invoice) => invoice.status === 'Partial').length;
  const overdue = stats.overdueInvoiceCount;
  const unpaid = Math.max(stats.invoicesDueCount - overdue - partial, 0);
  const cleared = Math.max(stats.invoicesCount - stats.invoicesDueCount, 0);
  const total = Math.max(cleared + partial + overdue + unpaid, 1);
  const paidPercent = formatNumber(cleared / total, locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  const segmentValue: Record<SegmentKey, number> = { paid: cleared, overdue, partial, unpaid };
  const segments = (Object.keys(SEGMENT_TOKEN) as SegmentKey[]).map((key) => ({
    key,
    label: t(`home:invoiceRing.${key}`),
    value: segmentValue[key],
  }));

  // Derived, so the donut can only ever paint what the legend names. The label is the one thing the
  // config adds — the tooltip prints translated state names, not the literal keys.
  const config: ChartConfig = Object.fromEntries(
    segments.map((segment) => [
      segment.key,
      {
        label: segment.label,
        colors: { light: [SEGMENT_TOKEN[segment.key]], dark: [SEGMENT_TOKEN[segment.key]] },
      },
    ]),
  );
  // A zero sector draws nothing and still claims a slot in the legend the chart renders, so it is
  // dropped from the chart data while staying in the list below, where "0 overdue" is good news
  // worth reading.
  const chartData = segments.filter((segment) => segment.value > 0);
  const chartLabel = t('home:invoiceRing.chartLabel', { paid: cleared, overdue, partial, unpaid });

  return (
    <HomeCard
      headingId="home-invoice-ring-heading"
      title={t('home:invoiceRing.heading')}
      description={t('home:invoiceRing.subheading')}
      action={
        <Link to="/invoices" search={{ show_archived: false }} className={HOME_CARD_LINK}>
          {t('home:invoiceRing.reviewLink', { count: stats.invoicesDueCount })}
        </Link>
      }
    >
      <div className="mt-3 grid gap-4 sm:grid-cols-[10rem_1fr] sm:items-center xl:grid-cols-1">
        <div>
          <div className="font-display font-semibold text-3xl text-foreground tabular-nums">
            {paidPercent}
          </div>
          <div className="text-muted-foreground text-xs">{t('home:invoiceRing.centerLabel')}</div>
          <div aria-hidden="true" className="mt-2">
            <EvilPieChart
              className="h-32 w-full"
              config={config}
              data={chartData}
              dataKey="value"
              nameKey="key"
            >
              <EvilPieChart.Pie innerRadius="60%" paddingAngle={2} />
              <EvilPieChart.Tooltip />
            </EvilPieChart>
          </div>
        </div>

        <dl className="grid gap-2">
          {segments.map((segment) => (
            <div key={segment.key} className="flex items-center justify-between gap-3 text-sm">
              <dt className="flex min-w-0 items-center gap-2 text-muted-foreground">
                <span
                  className="size-2.5 rounded-full"
                  style={{ background: SEGMENT_TOKEN[segment.key] }}
                  aria-hidden="true"
                />
                <span className="truncate">{segment.label}</span>
              </dt>
              <dd className="font-semibold text-foreground tabular-nums">
                {formatNumber(segment.value, locale)}
              </dd>
            </div>
          ))}
        </dl>
        <p className="sr-only">{chartLabel}</p>
      </div>
    </HomeCard>
  );
}
